import React, { createContext, useState, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { jwtDecode } from 'jwt-decode';
import toast from 'react-hot-toast';
import { setCurrency, resetCurrency } from '../config/currency';

interface User {
  id: number
  username: string
  email: string
  roles: string[]
  /** When true, the user is a steward restricted to Income/Expense/EOD add-only access. */
  financeOnlySteward?: boolean
  /** Optional post-login landing path override returned by the backend (e.g. "/vendor/finance"). */
  defaultLandingPath?: string | null
}

interface GuestSession {
  sessionToken: string;
  jwtToken?: string;  // JWT auth token (different from sessionToken for WhatsApp sessions)
  phoneNumber: string;
  name?: string;
  verified: boolean;
  expiresAt: string;
  isActive: boolean;
}

interface WhatsAppTokenResponse {
  sessionToken: string;
  jwtToken: string;
  phoneNumber: string;
  vendorId: number;
  qrCode: string;
  verified: boolean;
  expiresAt: string;
}

interface AuthContextType {
  user: User | null
  guestSession: GuestSession | null
  token: string | null
  isAuthenticated: boolean
  isGuest: boolean
  hasRole: (role: string) => boolean
  isVendor: boolean
  isSteward: boolean
  isAdmin: boolean
  signin: (username: string, password: string) => Promise<void>
  signinSteward: (stewardCode: string) => Promise<void>
  signup: (username: string, email: string, password: string) => Promise<void>
  signout: () => void;
  handleSocialLogin: (token: string) => Promise<void>
  requestGuestSession: (phoneNumber: string, name?: string) => Promise<{ sessionToken: string; message: string }>
  verifyGuestSession: (sessionToken: string, verificationCode: string) => Promise<void>
  setWhatsAppGuestSession: (response: WhatsAppTokenResponse) => void
  clearGuestSession: () => void
  clearAllAuthState: () => void
  validateSessionWithBackend: () => Promise<boolean>
  /**
   * Refreshes the cached User from POST /auth/refresh-claims. Useful when the
   * backend toggles a permission flag (e.g. financeOnlySteward) and we want the
   * UI to reflect it without forcing a logout/login round-trip.
   */
  refreshClaims: () => Promise<void>
}

// Define the structure of the decoded JWT payload
interface DecodedToken {
  sub: string; // Subject (usually username or user ID from token, maps to our User.username)
  id: number;  // User ID
  email: string;
  roles: string[]; // Should be an array of strings like "ROLE_USER", "ROLE_ADMIN"
  iat?: number; // Issued at
  exp?: number; // Expiration time
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Persist token and user to localStorage
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token')
  )
  const [user, setUser] = useState<User | null>(() => {
    const raw = localStorage.getItem('user')
    return raw ? JSON.parse(raw) : null
  })
  
  // Guest session state with automatic cleanup
  const [guestSession, setGuestSession] = useState<GuestSession | null>(() => {
    const raw = localStorage.getItem('guestSession')
    if (!raw) return null;
    
    try {
      const session = JSON.parse(raw);
      
      // Check if session is expired
      const now = new Date();
      const expiresAt = new Date(session.expiresAt);
      
      if (now > expiresAt) {
        console.log('🧹 Removing expired guest session');
        localStorage.removeItem('guestSession');
        localStorage.removeItem('token'); // Also remove stale token
        return null;
      }
      
      // Check if session is unverified for too long (>10 minutes)
      if (!session.verified) {
        const createdAt = new Date(session.createdAt || Date.now());
        const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
        
        if (createdAt < tenMinutesAgo) {
          console.log('🧹 Removing old unverified guest session');
          localStorage.removeItem('guestSession');
          localStorage.removeItem('token');
          return null;
        }
      }
      
      return session;
    } catch {
      console.log('🧹 Removing corrupted guest session');
      localStorage.removeItem('guestSession');
      localStorage.removeItem('token');
      return null;
    }
  })

  const isAuthenticated = Boolean(token && (user || guestSession))
  const isGuest = Boolean(guestSession && !user)
  
  // Helper methods to check user roles
  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) || false;
  }
  
  const isVendor = hasRole('ROLE_VENDOR');
  const isSteward = hasRole('ROLE_STEWARD');
  const isAdmin = hasRole('ROLE_ADMIN');

  // UI state for global logout overlay
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Validate and sync auth state on app load
  useEffect(() => {
    const validateAuthState = async () => {
      const storedToken = localStorage.getItem('token');
      // const storedUser = localStorage.getItem('user');
      // const storedGuestSession = localStorage.getItem('guestSession');
      
      // If we have a guest session, validate it with backend
      if (guestSession && guestSession.verified) {
        const isValid = await validateSessionWithBackend();
        if (!isValid) {
          // Session is invalid, state has been cleared by validateSessionWithBackend
          return;
        }
        
        // Session is valid, sync token if needed
        // Prefer JWT token (WhatsApp sessions) over sessionToken (OTP sessions)
        const expectedToken = guestSession.jwtToken || guestSession.sessionToken;
        if (!token || token !== expectedToken) {
          console.log('🔄 Syncing guest session token');
          setToken(expectedToken);
          localStorage.setItem('token', expectedToken);
        }
      }
      
      // If we have a token but no valid session/user, clear everything
      if (token && !user && !guestSession) {
        console.log('🧹 Clearing orphaned token');
        setToken(null);
        localStorage.removeItem('token');
      }
      
      // If stored data doesn't match state, sync it
      if (storedToken !== token) {
        if (token) {
          localStorage.setItem('token', token);
        } else {
          localStorage.removeItem('token');
        }
      }
    };
    
    validateAuthState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, guestSession]);
  
  // Periodically validate guest sessions
  useEffect(() => {
    if (!guestSession || !guestSession.verified) return;
    
    // Validate session every 5 minutes
    const interval = setInterval(async () => {
      console.log('⏰ Periodic session validation');
      await validateSessionWithBackend();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestSession]);

  const signin = async (username: string, password: string) => {
    const response = await api.post('/auth/signin', { username, password })
    const {
      token: returnedToken,
      id,
      username: name,
      email,
      roles,
      currency,
      financeOnlySteward,
      defaultLandingPath,
    } = response.data
    const rawToken = returnedToken.startsWith('Bearer ')
      ? returnedToken.substring(7)
      : returnedToken
    localStorage.setItem('token', rawToken)
    setToken(rawToken)
    const userObj: User = {
      id,
      username: name,
      email,
      roles,
      financeOnlySteward: Boolean(financeOnlySteward),
      defaultLandingPath: defaultLandingPath ?? null,
    }
    localStorage.setItem('user', JSON.stringify(userObj))
    setUser(userObj)

    // Set vendor-specific currency (falls back to INR if not present)
    if (currency && currency.symbol && currency.code && currency.locale) {
      setCurrency(currency);
    }

    // Clear any existing guest session and cart
    clearGuestSession()
    localStorage.removeItem('cart-items'); // Clear cart on sign in

  }

  const signinSteward = async (stewardCode: string) => {
    const response = await api.post('/auth/steward/code-login', { stewardCode })
    const { token: returnedToken, user: userData } = response.data
    
    console.log('Steward login response:', { userData, roles: userData.roles });
    
    // Extract token (remove Bearer prefix if present)
    const rawToken = returnedToken.startsWith('Bearer ')
      ? returnedToken.substring(7)
      : returnedToken
    localStorage.setItem('token', rawToken)
    setToken(rawToken)
    
    // Store user data
    const userObj: User = {
      id: userData.id,
      username: userData.username,
      email: userData.email,
      roles: userData.roles,
      financeOnlySteward: Boolean(userData.financeOnlySteward),
      defaultLandingPath: userData.defaultLandingPath ?? null,
    }
    localStorage.setItem('user', JSON.stringify(userObj))
    setUser(userObj)
    
    console.log('Steward user set in context:', userObj);
    
    // Clear any existing guest session and cart
    clearGuestSession()
    localStorage.removeItem('cart-items'); // Clear cart on steward sign in
    
  }

  const signup = async (
    username: string,
    email: string,
    password: string
  ) => {
    await api.post('/auth/signup', { username, email, password })
  }

  const requestGuestSession = async (phoneNumber: string, name?: string) => {
    try {
      const response = await api.post('/guest-auth/request-verification', {
        phoneNumber: phoneNumber,
        name: name || 'Guest'
      });
      
      const { sessionToken, message } = response.data;
      
      // Store the session token temporarily (not verified yet)
      const tempSession: GuestSession = {
        sessionToken,
        phoneNumber: phoneNumber,
        name: name || 'Guest',
        verified: false,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
        isActive: false
      };
      
      localStorage.setItem('guestSession', JSON.stringify(tempSession));
      setGuestSession(tempSession);
      
      return { sessionToken, message };
    } catch (error) {
      console.error('Failed to request guest session:', error);
      throw new Error((error as Error & { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to request verification code. Please try again.');
    }
  };

  const verifyGuestSession = async (sessionToken: string, verificationCode: string) => {
    try {
      const response = await api.post('/guest-auth/verify', {
        sessionToken,
        verificationCode
      });
      
      const { verified, expiresAt, phoneNumber, jwtToken } = response.data;
      
      if (verified) {
        // Create verified guest session (preserve name from temp session)
        const verifiedSession: GuestSession = {
          sessionToken,
          phoneNumber,
          name: guestSession?.name || 'Guest',
          verified: true,
          expiresAt,
          isActive: true
        };
        
        localStorage.setItem('guestSession', JSON.stringify(verifiedSession));
        setGuestSession(verifiedSession);
        
        // Set the JWT token (if provided) or session token as the auth token
        const authToken = jwtToken || sessionToken;
        localStorage.setItem('token', authToken);
        setToken(authToken);
        
        console.log('✅ Guest session verified and token synchronized');
        
        // Clear any existing user state
        setUser(null);
        localStorage.removeItem('user');
        
      }
    } catch (error) {
      console.error('Failed to verify guest session:', error);
      throw new Error((error as Error & { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to verify code. Please try again.');
    }
  };

  const setWhatsAppGuestSession = (response: WhatsAppTokenResponse) => {
    const session: GuestSession = {
      sessionToken: response.sessionToken,
      jwtToken: response.jwtToken,  // Store JWT separately from sessionToken
      phoneNumber: response.phoneNumber,
      name: 'WhatsApp Guest',
      verified: true,
      expiresAt: response.expiresAt,
      isActive: true
    };

    localStorage.setItem('guestSession', JSON.stringify(session));
    setGuestSession(session);

    const authToken = response.jwtToken || response.sessionToken;
    localStorage.setItem('token', authToken);
    setToken(authToken);

    // Clear any existing user state
    setUser(null);
    localStorage.removeItem('user');

    console.log('WhatsApp guest session set successfully');
  };

  const clearGuestSession = () => {
    localStorage.removeItem('guestSession');
    setGuestSession(null);
  };

  const clearAllAuthState = () => {
    // Clear all authentication-related localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('guestSession');
    localStorage.removeItem('qrCode');
    localStorage.removeItem('vendorId');
    localStorage.removeItem('pendingOrderId');
    localStorage.removeItem('cart-items'); // Clear cart items from localStorage
    resetCurrency(); // Reset to default INR

    // Clear auth state
    setToken(null);
    setUser(null);
    setGuestSession(null);

    console.log('🧹 All authentication state cleared');
  };

  const refreshClaims = async () => {
    if (!token) return;
    try {
      const response = await api.post('/auth/refresh-claims', {});
      const data = response.data || {};
      const refreshed: User = {
        id: data.id ?? user?.id ?? 0,
        username: data.username ?? user?.username ?? '',
        email: data.email ?? user?.email ?? '',
        roles: Array.isArray(data.roles) ? data.roles : (user?.roles ?? []),
        financeOnlySteward: Boolean(data.financeOnlySteward),
        defaultLandingPath: data.defaultLandingPath ?? null,
      };
      localStorage.setItem('user', JSON.stringify(refreshed));
      setUser(refreshed);
      if (data.token) {
        const rawToken = String(data.token).startsWith('Bearer ')
          ? String(data.token).substring(7)
          : data.token;
        localStorage.setItem('token', rawToken);
        setToken(rawToken);
      }
      if (data.currency && data.currency.symbol && data.currency.code && data.currency.locale) {
        setCurrency(data.currency);
      }
    } catch (err) {
      console.warn('Failed to refresh auth claims:', err);
    }
  };

  const validateSessionWithBackend = async () => {
    if (!guestSession || !guestSession.verified) return false;
    
    try {
      const response = await api.get(`/guest-auth/status?sessionToken=${guestSession.sessionToken}`);
      const isActive = response.data.active === true;
      
      if (!isActive) {
        console.log('🧹 Session expired on backend, clearing local state');
        clearGuestSession();
        // Clear token as well since session is invalid
        localStorage.removeItem('token');
        setToken(null);
      }
      
      return isActive;
    } catch {
      console.log('🧹 Session invalid on backend, clearing local state');
      clearGuestSession();
      // Clear token as well since session is invalid
      localStorage.removeItem('token');
      setToken(null);
      return false;
    }
  };

  const navigate = useNavigate();

  const signout = async () => {
    // Unified login page for all users
    const redirectPath = '/login';
    const toastId = toast.loading('Logging out...');
    setIsLoggingOut(true);
    try {
      // Server-side token revocation (audit #9): tell the backend to bump
      // users.token_invalidated_after so this JWT — and any other copies of
      // it — are rejected on the next request, even though the JWT itself
      // is still cryptographically valid until its 24h expiry.
      if (token) {
        try {
          await api.post('/auth/logout', {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (error) {
          // Non-fatal: even if backend logout fails, we still clear local state
          console.warn('Backend logout call failed (continuing with local cleanup):', error);
        }
      }
    } catch (error) {
      console.warn('Error during logout cleanup:', error);
    }
    
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('guestSession');
    localStorage.removeItem('qrCode');
    localStorage.removeItem('vendorId');
    localStorage.removeItem('cart-items'); // Clear cart items from localStorage
    resetCurrency(); // Reset to default INR
    // Clear any stored previous paths across zones to avoid cross-session back
    localStorage.removeItem('previousPath:CUSTOMER');
    localStorage.removeItem('previousPath:VENDOR');
    localStorage.removeItem('previousPath:STEWARD');
    // Clear role-scoped navigation stacks
    sessionStorage.removeItem('navStack:CUSTOMER');
    sessionStorage.removeItem('navStack:VENDOR');
    sessionStorage.removeItem('navStack:STEWARD');
    sessionStorage.removeItem('navBack:CUSTOMER');
    sessionStorage.removeItem('navBack:VENDOR');
    sessionStorage.removeItem('navBack:STEWARD');
    
    // Clear auth state
    setToken(null);
    setUser(null);
    setGuestSession(null);
    
    // Briefly show overlay, then dismiss and navigate so nothing lingers on the login screen
    setTimeout(() => {
      setIsLoggingOut(false);
      toast.dismiss(toastId);
      navigate(redirectPath, { replace: true });
    }, 500);
  };

  const handleSocialLogin = async (receivedToken: string) => {
    try {
      const decodedToken: DecodedToken = jwtDecode(receivedToken);
      const loggedInUser: User = {
        id: decodedToken.id,
        username: decodedToken.sub, // 'sub' from JWT is typically the username or a unique identifier
        email: decodedToken.email,
        roles: decodedToken.roles, // Ensure roles are in the expected format
      };

      localStorage.setItem('token', receivedToken);
      localStorage.setItem('user', JSON.stringify(loggedInUser));
      setToken(receivedToken);
      setUser(loggedInUser);
      
      // Clear any existing guest session and cart
      clearGuestSession();
      localStorage.removeItem('cart-items'); // Clear cart on social login
      
    } catch (error) {
      console.error("Failed to decode token or set auth state:", error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        guestSession, 
        token, 
        isAuthenticated, 
        isGuest,
        hasRole,
        isVendor,
        isSteward,
        isAdmin,
        signin,
        signinSteward, 
        signup, 
        signout, 
        handleSocialLogin, 
        requestGuestSession,
        verifyGuestSession,
        setWhatsAppGuestSession,
        clearGuestSession,
        clearAllAuthState,
        validateSessionWithBackend,
        refreshClaims
      }}
    >
      {children}
      {isLoggingOut && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-t-transparent border-[#B37A4C]"></div>
            <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-200">Logging you out...</p>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  )
}

const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Export all hooks and components
export { AuthProvider, useAuth }

